import { useState, useEffect } from 'react';
import { fetchPublicSettings } from '../utils/publicApi';

interface PublicSettings {
  companyName: string;
  companyLogo: string;
}

export const usePublicSettings = () => {
  const [settings, setSettings] = useState<PublicSettings>({
    companyName: 'EV Charging', // Default fallback
    companyLogo: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    const loadSettings = async () => {
      try {
        // Add timeout to prevent infinite loading
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Settings fetch timeout')), 5000);
        });
        
        const settingsPromise = fetchPublicSettings();
        const publicSettings = await Promise.race([settingsPromise, timeoutPromise]);
        
        if (mounted) {
          setSettings({
            companyName: publicSettings.company_name,
            companyLogo: publicSettings.company_logo,
          });
        }
      } catch (error) {
        console.error('Failed to load public settings:', error);
        // Keep default values on error
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadSettings();
    
    return () => {
      mounted = false;
    };
  }, []); // Only run once on mount

  // Listen for public settings update events
  useEffect(() => {
    const handleSettingsUpdate = async () => {
      try {
        const publicSettings = await fetchPublicSettings();
        setSettings({
          companyName: publicSettings.company_name,
          companyLogo: publicSettings.company_logo,
        });
      } catch (error) {
        console.error('Failed to refresh public settings:', error);
      }
    };

    window.addEventListener('publicSettingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('publicSettingsUpdated', handleSettingsUpdate);
  }, []);

  return { settings, loading };
};